/**
 * THE REFERENCE INTO THE KIT: what a document read writes onto a brand kit, field by field, and what it
 * leaves alone.
 *
 * The lab reads a PDF into a DesignReference (colours with hex and role, families, a scale with sizes on
 * the 1920 canvas, spacing steps, radius, rules, voice) with a source on every value. The kit stores the
 * same facts across two records: `profile` (colour groups, fonts, prose, rules) and `brandStyle` (the
 * measured half: palette, roles, type scale, spacing ramp, radius). This module maps one onto the other
 * so the recipe every generation reads (`styleToThemeRecipe`) carries the document's system without any
 * other code learning what a DesignReference is.
 *
 * THE REFERENCE WINS ON AN EXPLICIT READ, with a changed-fields report (the approved plan, 2026-09-05).
 * Colours: the document's come first in every group, named as the document names them; colours the kit
 * held that the document does not carry stay, after them. Fonts, the scale, spacing and radius: the
 * document's measured values replace the kit's. Prose that a person may have written by hand (tone,
 * taste, guideline, notes) is filled only when empty, and the report says what was kept.
 *
 * Pure: no React, no fetch, no Node. Safe for the client, the server and a test.
 */
import type { BrandKit } from '../birtvy/brandKit'
import type { BrandRule, BrandStyle, MoodboardProfile } from '../types/presentation'
import { COLOR_GROUPS, colorsOf, projectColorGroups, type BrandColor, type ColorGroupId } from '../kitAgent/kitEdits'
import { normalizeHex } from '../kitAgent/kitColorMath'
import { buildScaleFor, resolveTypeRole, weightFromWord, type KitFontFace } from '../kitAgent/kitTypographyOps'
import { clampScaleToBands, snapScaleWeights, TYPE_ROLES, weightsForFamily, type TypeRole, type TypeScale, type TypeStyle } from '../style/typeScale'
import type { DesignReference, RefFamily, RefScaleRow } from './designReference'
import type { LayoutPage, LayoutReference } from './layoutReference'

/* ── the record the kit keeps ────────────────────────────────────────────────────────────────────── */

/**
 * What the kit remembers of the document it was read from: the reference itself (the DESIGN.md), the
 * layout catalogue once the pages were analysed (the LAYOUT.md), and where the file lives on the server
 * while it lasts. Rides `profile.document`, the way the site capture rides `profile.site`.
 */
export interface KitDocumentRecord {
  fileName: string
  at: number
  /** The document store id on this server. Page reads and renders are keyed by it; absent after a restart. */
  docId?: string
  model?: string
  pageCount: number
  reference: DesignReference
  layout?: LayoutReference
}

export type ProfileWithDocument = MoodboardProfile & { document?: KitDocumentRecord }

/** Defensive read, as `readSiteCapture` is: a malformed record answers null rather than a crash. */
export function readKitDocument(profile: MoodboardProfile | null | undefined): KitDocumentRecord | null {
  const raw = (profile as ProfileWithDocument | null | undefined)?.document
  if (!raw || typeof raw !== 'object') return null
  const ref = raw.reference
  if (!ref || typeof ref !== 'object' || !Array.isArray(ref.colours) || !Array.isArray(ref.families)) return null
  return {
    fileName: typeof raw.fileName === 'string' && raw.fileName ? raw.fileName : 'the document',
    at: typeof raw.at === 'number' ? raw.at : 0,
    ...(typeof raw.docId === 'string' && raw.docId ? { docId: raw.docId } : {}),
    ...(typeof raw.model === 'string' && raw.model ? { model: raw.model } : {}),
    pageCount: typeof raw.pageCount === 'number' ? raw.pageCount : 0,
    reference: ref,
    ...(raw.layout && typeof raw.layout === 'object' && Array.isArray(raw.layout.pages) ? { layout: raw.layout } : {}),
  }
}

export function withKitDocument(profile: MoodboardProfile, record: KitDocumentRecord | null): MoodboardProfile {
  const rest: ProfileWithDocument = { ...(profile as ProfileWithDocument) }
  delete rest.document
  return record ? ({ ...rest, document: record } as MoodboardProfile) : (rest as MoodboardProfile)
}

/** A deck binding snapshots the kit; the document is the live kit's business, like the references. */
export function stripKitDocument(profile: MoodboardProfile): MoodboardProfile {
  return withKitDocument(profile, null)
}

/** The longest a page's free-text fields ride in the stored catalogue. The profile is one JSON column. */
const LAYOUT_TEXT_CAP = 160

/** The catalogue without the page-by-page prose a copy needs and a planner does not. */
export function compactLayout(layout: LayoutReference): LayoutReference {
  const trim = (s: string | undefined) => (s && s.length > LAYOUT_TEXT_CAP ? `${s.slice(0, LAYOUT_TEXT_CAP - 1)}…` : s)
  return {
    ...layout,
    pages: layout.pages.map((p) => {
      const rest: Partial<LayoutPage> & typeof p = { ...p }
      delete rest.reproduction
      return {
        ...rest,
        ...(rest.imagery ? { imagery: trim(rest.imagery) } : {}),
        ...(rest.whitespace ? { whitespace: trim(rest.whitespace) } : {}),
        ...(rest.chrome ? { chrome: trim(rest.chrome) } : {}),
        ...(rest.grid ? { grid: trim(rest.grid) } : {}),
        ...(rest.sample ? { sample: trim(rest.sample) } : {}),
      }
    }),
  }
}

/* ── colours ─────────────────────────────────────────────────────────────────────────────────────── */

/** The document's colour role (`primary`, `text-muted`, `signal`…) onto the kit's eight groups. */
export function colourGroupOf(role: string): ColorGroupId {
  const r = role.trim().toLowerCase()
  if (/background|canvas|paper|page\b/.test(r)) return 'background'
  if (/text|ink|foreground|type\b/.test(r)) return 'text'
  if (/accent|primary|brand|highlight|signal|cta|action|link|secondary/.test(r)) return 'accent'
  if (/surface|panel|fill|tint|neutral/.test(r)) return 'surface'
  if (/card/.test(r)) return 'card'
  if (/border|rule|line|divider|stroke/.test(r)) return 'border'
  if (/icon/.test(r)) return 'icon'
  return 'other'
}

const hexKey = (v: string): string => (normalizeHex(v) ?? v.trim()).toLowerCase()

/** A name the document gave, or nothing when the reference only numbered a measured fill. */
function namedBy(ref: DesignReference['colours'][number]): string | null {
  const name = ref.name.trim()
  if (!name || hexKey(name) === ref.hex || /^(painted|text|colour|color) \d+$/i.test(name)) return null
  return name
}

export interface MergedColours {
  colours: BrandColor[]
  added: BrandColor[]
  /** Colours the kit already held that took the document's name or group. */
  renamed: number
}

/**
 * The document's colours first, in every group, the kit's others after them.
 *
 * Matched by hex, so a colour the kit already holds keeps its id (the logo cards reference it) and takes
 * the document's name and group. A new hex gets an id from its own value, so a second read of the same
 * document is the identity rather than a second copy.
 */
export function mergeReferenceColours(existing: readonly BrandColor[], ref: DesignReference): MergedColours {
  const byHex = new Map(existing.map((c) => [hexKey(c.value), c] as const))
  const fromRef: BrandColor[] = []
  const used = new Set<string>()
  const added: BrandColor[] = []
  let renamed = 0
  /** "Text 2": one past every colour already standing in that group, the document's and the kit's own. */
  const positionIn = (group: ColorGroupId) =>
    fromRef.filter((c) => c.group === group).length + existing.filter((c) => c.group === group && !used.has(c.id)).length + 1
  for (const c of ref.colours) {
    const hex = hexKey(c.hex)
    if (!normalizeHex(hex) || fromRef.some((f) => hexKey(f.value) === hex)) continue
    const mapped = colourGroupOf(c.role)
    const name = namedBy(c)
    const held = byHex.get(hex)
    if (held) {
      const group = mapped === 'other' ? held.group : mapped
      const next: BrandColor = { ...held, name: name ?? held.name, group }
      if (next.name !== held.name || next.group !== held.group) renamed += 1
      fromRef.push(next)
      used.add(held.id)
      continue
    }
    const spec = COLOR_GROUPS.find((g) => g.id === mapped)
    const fresh: BrandColor = {
      id: `doc-${hex.slice(1)}`,
      name: name ?? `${spec?.singular ?? 'Color'} ${positionIn(mapped)}`,
      value: hex,
      group: mapped,
    }
    fromRef.push(fresh)
    added.push(fresh)
  }
  const rest = existing.filter((c) => !used.has(c.id))
  const all = [...fromRef, ...rest]
  return { colours: COLOR_GROUPS.flatMap((g) => all.filter((c) => c.group === g.id)), added, renamed }
}

/* ── type ────────────────────────────────────────────────────────────────────────────────────────── */

const DISPLAY_ROLE = /display|head|title|hero|cover|statement/i
const BODY_ROLE = /body|paragraph|text|copy|running|prose/i

/**
 * A family a page can ask for by name. A read off page images often hedges, "Inter / SF Pro Display",
 * and that string is two guesses rather than a family, so it takes the substitute the read gave, or else
 * the first guess. A single name stays exactly as the document set it.
 */
export function loadableFamily(f: Pick<RefFamily, 'family' | 'substitute'>): string {
  const guesses = f.family.split(/\s+\/\s+/).map((name) => name.trim()).filter(Boolean)
  if (guesses.length < 2) return f.family.trim()
  return f.substitute?.trim() || guesses[0]
}

/**
 * Which family carries the display and which the body. The type scale says which family each size is
 * set in, so its display and body rows decide when they name one. Otherwise the roles the document gave
 * the families decide, and a description is weaker evidence: "eyebrow headers" reads as a heading role.
 */
export function familiesFromReference(ref: DesignReference): { display?: string; body?: string } {
  const named = ref.families.filter((f) => f.family.trim() && !f.anonymous)
  const pool = named.length ? named : ref.families.filter((f) => f.family.trim())
  if (pool.length === 0) return {}
  const setIn = (role: TypeRole) => {
    const row = ref.scale.find((r) => r.family?.trim() && scaleRoleOf(r) === role)
    return row ? pool.find((f) => f.family.trim() === row.family!.trim()) : undefined
  }
  const scaleDisplay = setIn('display') ?? setIn('heading')
  const scaleBody = setIn('body')
  if (scaleDisplay || scaleBody) {
    return { display: loadableFamily((scaleDisplay ?? scaleBody)!), body: loadableFamily((scaleBody ?? scaleDisplay)!) }
  }
  const rank = (f: RefFamily) => (f.source === 'measured' ? 0 : f.source === 'printed' ? 1 : 2)
  const sorted = [...pool].sort((a, b) => rank(a) - rank(b))
  const display = sorted.find((f) => DISPLAY_ROLE.test(f.role) && !BODY_ROLE.test(f.role)) ?? sorted.find((f) => DISPLAY_ROLE.test(f.role))
  const body = sorted.find((f) => BODY_ROLE.test(f.role) && f !== display) ?? sorted.find((f) => BODY_ROLE.test(f.role))
  if (display || body) return { display: loadableFamily((display ?? body)!), body: loadableFamily((body ?? display)!) }
  // No roles said: the family set in the largest sizes is the display, the one in the most sizes the body.
  const biggest = [...sorted].sort((a, b) => (b.sizesPt[0] ?? 0) - (a.sizesPt[0] ?? 0))[0]
  const busiest = [...sorted].sort((a, b) => b.sizesPt.length - a.sizesPt.length)[0]
  return { display: loadableFamily(biggest), body: loadableFamily(sorted.length > 1 && busiest !== biggest ? busiest : biggest) }
}

const SCALE_ROLE_WORDS: [RegExp, TypeRole][] = [
  [/^(display|hero|title|cover)/i, 'display'],
  [/^(headline|heading|h1|section)/i, 'heading'],
  [/^(sub|h2|h3|eyebrow|kicker|intro|lead|standfirst|deck)/i, 'subheading'],
  [/^(body|paragraph|text|prose|copy|running)/i, 'body'],
  [/^(caption|label|small|footnote|footer|source|note|legal|meta|micro|folio|page)/i, 'caption'],
]

function scaleRoleOf(row: RefScaleRow): TypeRole | null {
  const name = row.role.trim()
  if (!name || name === 'unsaid') return null
  const exact = resolveTypeRole(name)
  if (exact) return exact
  for (const [re, role] of SCALE_ROLE_WORDS) if (re.test(name)) return role
  return null
}

function weightOf(raw: string | undefined): number | undefined {
  if (!raw) return undefined
  const n = Number.parseInt(raw, 10)
  if (Number.isFinite(n) && n >= 100 && n <= 900) return Math.round(n / 100) * 100
  const word = weightFromWord(raw.replace(/[^a-z]/gi, ''))
  return word ?? undefined
}

/** A tracking string as em: `-0.02em`, `-2%`, `-20` (thousandths, as InDesign prints them), `0.01`. */
export function trackingEm(raw: string | undefined): number | undefined {
  if (!raw) return undefined
  const m = raw.trim().match(/^(-?\d*\.?\d+)\s*(em|%|pt|px)?$/i)
  if (!m) return undefined
  const n = Number(m[1])
  const unit = (m[2] ?? '').toLowerCase()
  if (!Number.isFinite(n)) return undefined
  if (unit === 'em') return n
  if (unit === '%') return n / 100
  if (unit === '') return Math.abs(n) < 1 ? n : Math.abs(n) <= 200 ? n / 1000 : undefined
  return undefined
}

const sizePxOf = (row: RefScaleRow, canvasScale: number): number | undefined =>
  typeof row.sizePx === 'number' ? row.sizePx : typeof row.sizePt === 'number' ? Math.round(row.sizePt * canvasScale) : undefined

export interface ReferenceScale {
  scale: TypeScale
  /** The roles the document decided; the rest are the base's. */
  measured: TypeRole[]
}

/**
 * The document's scale rows onto the five roles, over a base scale, held in the contract's bands.
 *
 * Rows name their role in the document's own words; those that resolve take their role, those that do
 * not fill the roles left empty by size, largest first, only while that keeps the scale in size order.
 * Sizes are already on the 1920 canvas (`sizePx`), and the bands clamp them the way every stored scale
 * is clamped: the brand decides where inside a band it sits, the contract decides the band.
 */
export function typeScaleFromReference(
  ref: DesignReference,
  base: TypeScale,
  families: { display?: string; body?: string },
  faces?: readonly KitFontFace[] | null,
): ReferenceScale {
  const rows = ref.scale.filter((r) => sizePxOf(r, ref.canvasScale) !== undefined)
  const picked = new Map<TypeRole, RefScaleRow>()
  const leftover: RefScaleRow[] = []
  for (const row of rows) {
    const role = scaleRoleOf(row)
    if (role && !picked.has(role)) picked.set(role, row)
    else if (!role) leftover.push(row)
  }
  // Unnamed rows fill the empty roles by rank, but never out of size order with the rows already placed.
  leftover.sort((a, b) => sizePxOf(b, ref.canvasScale)! - sizePxOf(a, ref.canvasScale)!)
  for (const role of TYPE_ROLES) {
    if (picked.has(role) || leftover.length === 0) continue
    const above = TYPE_ROLES.slice(0, TYPE_ROLES.indexOf(role)).map((r) => picked.get(r)).filter(Boolean).pop()
    const below = TYPE_ROLES.slice(TYPE_ROLES.indexOf(role) + 1).map((r) => picked.get(r)).find(Boolean)
    const fits = (row: RefScaleRow) => {
      const px = sizePxOf(row, ref.canvasScale)!
      return (!above || px < sizePxOf(above, ref.canvasScale)!) && (!below || px > sizePxOf(below, ref.canvasScale)!)
    }
    const i = leftover.findIndex(fits)
    if (i === -1) continue
    picked.set(role, leftover[i])
    leftover.splice(i, 1)
  }

  const out: TypeScale = JSON.parse(JSON.stringify(base))
  const measured: TypeRole[] = []
  const bodyFamily = families.body?.toLowerCase()
  const displayFamily = families.display?.toLowerCase()
  for (const role of TYPE_ROLES) {
    const row = picked.get(role)
    if (!row) continue
    const size = sizePxOf(row, ref.canvasScale)!
    const family = row.family?.trim().toLowerCase()
    const face: TypeStyle['face'] | undefined =
      family && bodyFamily && displayFamily && bodyFamily !== displayFamily
        ? family.includes(bodyFamily) || bodyFamily.includes(family)
          ? 'body'
          : family.includes(displayFamily) || displayFamily.includes(family)
            ? 'display'
            : undefined
        : undefined
    const weight = weightOf(row.weight)
    const tracking = trackingEm(row.tracking)
    const leading = typeof row.leading === 'number' && row.leading >= 0.8 && row.leading <= 2.5 ? row.leading : undefined
    out[role] = {
      ...out[role],
      size,
      ...(face ? { face } : {}),
      ...(weight !== undefined ? { weight } : {}),
      ...(tracking !== undefined ? { tracking } : {}),
      ...(leading !== undefined ? { leading } : {}),
      ...(row.case ? { case: /upper|caps/i.test(row.case) ? 'upper' : 'none' } : {}),
    }
    measured.push(role)
  }
  const snapped = snapScaleWeights(clampScaleToBands(out), {
    displayWeights: weightsForFamily(faces, families.display),
    bodyWeights: weightsForFamily(faces, families.body),
  })
  return { scale: snapped, measured }
}

/* ── spacing, radius, prose ──────────────────────────────────────────────────────────────────────── */

/** The spacing ramp as CSS lengths on the canvas, the shape `BrandStyle.spacing` stores, plus the sentence. */
export function spacingFromReference(ref: DesignReference): { ramp: string[]; sentence: string } {
  const px = [...new Set(ref.spacing.scale.map((s) => s.px ?? Math.round(s.pt * ref.canvasScale)).filter((n) => Number.isFinite(n) && n >= 4 && n <= 400))].sort((a, b) => a - b)
  const bits: string[] = []
  if (ref.spacing.baseUnit) bits.push(`Base unit ${ref.spacing.baseUnit}`)
  if (px.length) bits.push(`spacing steps ${px.map((n) => `${n}px`).join(', ')} on the 1920 canvas`)
  if (ref.spacing.margins) bits.push(`margins ${ref.spacing.margins}`)
  if (ref.spacing.density) bits.push(`${ref.spacing.density} density`)
  if (ref.spacing.sectionGap) bits.push(`section gap ${ref.spacing.sectionGap}`)
  if (ref.spacing.elementGap) bits.push(`element gap ${ref.spacing.elementGap}`)
  return { ramp: px.map((n) => `${n}px`), sentence: bits.length ? `${bits.join('; ')}.` : '' }
}

/** The first radius the document states, as px on the canvas. `0` when it says square. */
export function radiusFromReference(ref: DesignReference): string | undefined {
  for (const r of ref.radius) {
    const value = r.value.trim().toLowerCase()
    if (!value || value === 'unsaid') continue
    if (/^(0|none|square|sharp)/.test(value)) return '0px'
    const m = value.match(/(-?\d*\.?\d+)\s*(px|pt|mm|rem)?/)
    if (!m) continue
    const n = Number(m[1])
    if (!Number.isFinite(n) || n < 0) continue
    const unit = m[2] ?? 'pt'
    const px = unit === 'px' ? n : unit === 'pt' ? n * ref.canvasScale : unit === 'mm' ? n * 2.835 * ref.canvasScale : n * 16
    return `${Math.round(px)}px`
  }
  return undefined
}

/** The type system as one sentence for the guide, sizes on the canvas. */
export function typographySentence(ref: DesignReference, families: { display?: string; body?: string }): string {
  const bits: string[] = []
  if (families.display && families.body && families.display !== families.body) bits.push(`${families.display} for display, ${families.body} for body`)
  else if (families.display) bits.push(`${families.display} throughout`)
  const rows = ref.scale.filter((r) => sizePxOf(r, ref.canvasScale) !== undefined).slice(0, 6)
  if (rows.length) {
    bits.push(
      rows
        .map((r) => {
          const parts = [`${r.role} ${sizePxOf(r, ref.canvasScale)}px`]
          if (r.weight) parts.push(`weight ${r.weight}`)
          if (r.leading) parts.push(`leading ${r.leading}`)
          if (r.tracking) parts.push(`tracking ${r.tracking}`)
          if (r.case) parts.push(`${r.case} case`)
          return parts.join(' ')
        })
        .join('; '),
    )
  }
  for (const rule of ref.typeRules.slice(0, 3)) bits.push(rule)
  return bits.length ? `${bits.join('. ')}.` : ''
}

/* ── the whole write ─────────────────────────────────────────────────────────────────────────────── */

/** The names a kit carries when nobody has named it. Kept in step with the kit page's `NEW_KIT_NAME`. */
export const PLACEHOLDER_KIT_NAMES: ReadonlySet<string> = new Set(['', 'untitled kit', 'new kit', 'brand kit'])

export interface ApplyReferenceOptions {
  fileName: string
  docId?: string
  model?: string
  pageCount: number
  layout?: LayoutReference | null
  at?: number
  /** How many document rules join the kit's standing rules at most. */
  maxRules?: number
}

export interface AppliedReference {
  patch: {
    name?: string
    profile: MoodboardProfile
    brandStyle: BrandStyle
    sources: BrandKit['sources']
  }
  /** What moved, as short phrases for the outcome sentence: "added 12 colours", "set Helvetica Neue for display". */
  changed: string[]
  /** Prose the kit already had and kept, named, so the sentence can say the document's was not taken. */
  kept: string[]
}

const trimmed = (v: string | undefined | null): string => (typeof v === 'string' ? v.trim() : '')
const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`
/** Short lines into one paragraph: each ends in one full stop, none in two. */
const sentences = (parts: readonly string[]): string =>
  parts
    .map((p) => trimmed(p).replace(/[.\s]+$/, ''))
    .filter(Boolean)
    .map((p) => `${p}.`)
    .join(' ')

export function applyDesignReference(
  kit: Pick<BrandKit, 'name' | 'profile' | 'brandStyle' | 'sources'>,
  ref: DesignReference,
  opts: ApplyReferenceOptions,
): AppliedReference {
  const changed: string[] = []
  const kept: string[] = []
  const at = opts.at ?? Date.now()

  // Colours: the document's first, the kit's others after.
  const merged = mergeReferenceColours(colorsOf(kit.profile), ref)
  let profile: MoodboardProfile = projectColorGroups(kit.profile, merged.colours)
  if (merged.added.length) changed.push(`added ${plural(merged.added.length, 'colour')}`)
  if (merged.renamed) changed.push(`renamed ${plural(merged.renamed, 'colour')} the document's way`)

  // Fonts: the document's families, by the roles it gave them.
  const families = familiesFromReference(ref)
  const headingFont = families.display ?? kit.profile.headingFont
  const bodyFont = families.body ?? kit.profile.bodyFont
  if (headingFont && headingFont !== kit.profile.headingFont) changed.push(`set ${headingFont} for display`)
  if (bodyFont && bodyFont !== kit.profile.bodyFont) changed.push(`set ${bodyFont} for body`)
  profile = { ...profile, ...(headingFont ? { headingFont } : {}), ...(bodyFont ? { bodyFont } : {}) }

  // The scale: the document's rows over the stored scale, or over the defaults for these families.
  const faces = kit.brandStyle?.fontFaces?.map((f) => ({ family: f.family, weight: f.weight, style: f.style, src: f.url })) ?? []
  const baseScale = kit.brandStyle?.typeScale ?? buildScaleFor(headingFont ?? 'sans-serif', faces, bodyFont)
  const scaled = typeScaleFromReference(ref, baseScale, { display: headingFont, body: bodyFont }, faces)
  if (scaled.measured.length) {
    changed.push(`set the type scale (${scaled.measured.map((r) => `${r} ${scaled.scale[r].size}px`).join(', ')})`)
  }

  // Spacing and radius: the measured half.
  const spacing = spacingFromReference(ref)
  const radius = radiusFromReference(ref)
  if (spacing.ramp.length >= 2) changed.push(`set the spacing steps (${spacing.ramp.join(', ')})`)
  if (radius) changed.push(`set the radius to ${radius}`)

  const brandStyle: BrandStyle = {
    ...(kit.brandStyle ?? { palette: [], roles: {} }),
    palette: profile.palette ?? [],
    roles: profile.roles ?? {},
    ...(headingFont ? { headingFont } : {}),
    ...(bodyFont ? { bodyFont } : {}),
    ...(scaled.measured.length ? { typeScale: scaled.scale } : kit.brandStyle?.typeScale ? { typeScale: kit.brandStyle.typeScale } : {}),
    ...(spacing.ramp.length >= 2 ? { spacing: spacing.ramp } : {}),
    ...(radius ? { typography: { ...(kit.brandStyle?.typography ?? {}), radius } } : {}),
  }

  // Prose: filled where empty, kept where a person wrote it.
  const fill = (label: string, mine: string | undefined, theirs: string): string | undefined => {
    if (trimmed(mine)) {
      if (theirs && trimmed(mine) !== theirs) kept.push(label)
      return mine
    }
    if (theirs) changed.push(`filled in the ${label}`)
    return theirs || mine
  }
  const voice = ref.voice ? sentences([ref.voice.tone ?? '', ...(ref.voice.rules ?? [])]) : ''
  const taste = sentences([ref.tagline ?? '', ref.description ?? ''])
  const direction = sentences([
    ref.imagery?.style ?? '',
    ...(ref.imagery?.rules ?? []),
    ref.layout.grid ? `Grid: ${trimmed(ref.layout.grid)}` : '',
  ])
  const typography = typographySentence(ref, { display: headingFont, body: bodyFont })
  profile = {
    ...profile,
    tone: fill('tone of voice', kit.profile.tone, voice),
    tasteProfile: fill('taste', kit.profile.tasteProfile, taste) ?? '',
    guideline: fill('guideline', kit.profile.guideline, direction) ?? '',
    typography: fill('typography notes', kit.profile.typography, typography),
    spacing: fill('spacing notes', kit.profile.spacing, spacing.sentence),
  }

  // Layout moves the document repeats, for the structure vocabulary; standing rules from its dos and don'ts.
  const patterns = [...new Set([...(kit.profile.patterns ?? []), ...ref.layout.patterns.map(trimmed).filter(Boolean)])]
  if (patterns.length > (kit.profile.patterns ?? []).length) changed.push(`noted ${plural(patterns.length - (kit.profile.patterns ?? []).length, 'layout pattern')}`)
  const heldRules = new Set((kit.profile.rules ?? []).map((r) => trimmed(r.text).toLowerCase()))
  const newRules: BrandRule[] = [...ref.dos.map((d) => trimmed(d)), ...ref.donts.map((d) => `Never: ${trimmed(d)}`)]
    .filter((text) => text && !heldRules.has(text.toLowerCase()))
    .slice(0, opts.maxRules ?? 12)
    .map((text, i) => ({ id: `doc-rule-${at}-${i}`, text, scope: 'kit' as const, at, origin: 'agent' as const, reason: `From ${opts.fileName}` }))
  if (newRules.length) changed.push(`recorded ${plural(newRules.length, 'rule')} from the document`)
  profile = {
    ...profile,
    ...(patterns.length ? { patterns } : {}),
    ...(newRules.length ? { rules: [...(kit.profile.rules ?? []), ...newRules] } : {}),
  }

  // The record itself: what the kit was read from. The css is rebuilt on demand; it never rides the column.
  profile = withKitDocument(profile, {
    fileName: opts.fileName,
    at,
    ...(opts.docId ? { docId: opts.docId } : {}),
    ...(opts.model ? { model: opts.model } : {}),
    pageCount: opts.pageCount,
    reference: { ...ref, css: '' },
    ...(opts.layout ? { layout: compactLayout(opts.layout) } : {}),
  })

  const name = ref.brand.trim() && PLACEHOLDER_KIT_NAMES.has(kit.name.trim().toLowerCase()) ? ref.brand.trim() : undefined
  if (name) changed.push(`named the kit ${name}`)

  return {
    patch: {
      ...(name ? { name } : {}),
      profile,
      brandStyle,
      sources: { ...(kit.sources ?? {}), pdf: [...new Set([...(kit.sources?.pdf ?? []), opts.fileName])] },
    },
    changed,
    kept,
  }
}

/** Counted facts about a reference, for a strip or a sentence. Counts, never adjectives. */
export function describeReference(ref: DesignReference): string {
  const bits = [
    ref.colours.length ? plural(ref.colours.length, 'colour') : '',
    ref.families.length ? plural(ref.families.length, 'family') : '',
    ref.scale.length ? plural(ref.scale.length, 'type size') : '',
    ref.spacing.scale.length ? plural(ref.spacing.scale.length, 'spacing step') : '',
    ref.dos.length + ref.donts.length ? plural(ref.dos.length + ref.donts.length, 'rule') : '',
  ].filter(Boolean)
  return bits.join(', ').replace('1 family', '1 family').replace(/(\d+) family$/, '$1 families')
}
